<template>
  <!-- The bare Analysis path has no tab: it is not a page (router/notFound.js). -->
  <not-found v-if="isNotFound" />
  <v-container v-else fluid id="dashboard-wrapper" class="h-100 ps-4">
    <v-sheet class="d-flex flex-column h-100">
      <div class="d-flex align-center my-2">
        <v-btn-toggle
          background-color="transparent"
          :value="selectedDashboard"
          dense
          id="dashboard-tabs"
          color="primary"
          class="d-flex"
          style="gap: 8px; overflow-x: auto"
          mandatory>
          <div  v-for="(tab, index) in tabs" :key="index">
            <v-tooltip  top>
                  <template v-slot:activator="{ on, attrs }">
            <span  v-bind="attrs"
            v-on="on" >
              <v-btn
            :value="tab.index"
            :key="tab.name"
            :disabled="tab.disabled"
            :class="['',{'text--disabled':tab.disabled}]"
            :ripple="{ class: 'primary--text' }"
            outlined
            @click="$router.push(tab.path)">
            <!-- <v-icon small class="mr-1">{{ tab.icon }}</v-icon> -->
            <span
              class="me-2 tab-icon"
              :style="{
                '-webkit-mask-image': `url(${getIcons(tab.icon)})`,
                'mask-image': `url(${getIcons(tab.icon)})`
              }"
              aria-hidden="true"></span>
            <span> {{ tab.title }} </span>
          </v-btn>
            </span>
                  </template>
                  <span>{{tab.disabled?'Permission Required':tab.title}}</span>
                </v-tooltip>
          </div>

        </v-btn-toggle>
      </div>

      <keep-alive>
        <router-view class="flex-grow-1"></router-view>
      </keep-alive>
    </v-sheet>
  </v-container>
</template>

<script>
  import levelsService from '@/features/Admin/services/levels.service';
import { $has } from '@/utils';
import permissions from '@/features/Share/config/permissions';
import { isNotFoundRoute } from '@/router/notFound';
import NotFound from '@/views/NotFound.vue';

  export default {
    name: 'DashboardView',
    components: { NotFound },
    methods: {
      getIcons(name) {
        // Check if name already has an extension
        const hasExtension = /\.\w+$/.test(name);
        const fileName = hasExtension ? name : `${name}.svg`;
        return require(`@Assets/svgs/${fileName}`);
      },
    },
    created() {
      levelsService.fetchAssetLevelsParsed(this.$route.params.id).then((levels) => {
        this.$store.state.levels = levels;
      });
      this.$store.commit('SET_VIEWER_TYPE', '2d');
    },
    computed: {
      isNotFound() {
        return isNotFoundRoute(this.$route);
      },
      tabs() {
        return [
          {
            index: 0,
            path: `/${this.$route.params.id}/dashboard/inspection/ai-analysis`,
            title: 'AI Analysis',
            name: 'ai-analysis',
            routeNames: ['ai-analysis', 'ai-analysis-job'],
            icon: 'AI',
            icon_active: 'AI_white',
            // Dedicated permission is API ask 9 (AVA-531); reuse Inspections_AIInspect until it is seeded.
            disabled: !$has(permissions.Dashboard.Inspections_AIInspect),
          },
          {
            index: 1,
            path: `/${this.$route.params.id}/dashboard/inspection/corrosion-analyser`,
            title: 'Corrosion Analyzer',
            name: 'corrosion-analyser',
            icon: 'CorrosionAnalyser',
            icon_active: 'CorrosionAnalyser_white',
            disabled: !$has(permissions.Dashboard.Inspections_CorrosionAnalyser),
          },
        ];
      },
      routeTab() {
        return this.tabs.find((a) => (a.routeNames || [a.name]).includes(this.$route.name)) ?? null;
      },
      selectedDashboard() {
        if (this.routeTab && !this.routeTab.disabled) {
          return this.routeTab.index;
        }
        let tabIndex = this.tabs.filter(t=>!t.disabled)[0]?.index ?? null;

        return tabIndex
      },
    },
    watch: {
      selectedDashboard: {
        handler(newIndex) {
          if (newIndex === null) {
            this.$router.push('/');
          }
        },
        immediate: true,
      },
      // The menu opens AI Analysis; a user without its permission gets the first tab they may use instead.
      '$route.name': {
        handler() {
          if (!this.routeTab || !this.routeTab.disabled || this.selectedDashboard === null) return;
          this.$router.replace(this.tabs.find((t) => t.index === this.selectedDashboard).path);
        },
        immediate: true,
      },
    },
  };
</script>
<style lang="scss">
  #dashboard-wrapper {
    font-family: 'Popins', sans-serif;

    #dashboard-tabs {
      & .v-btn {
        text-transform: none !important;
        border-radius: 8px !important;
        letter-spacing: normal !important;
        font-weight: 500;
        font-size: 13px;
        height: 40px;
        padding: 0 16px;
        transition: all 0.2s ease;

        // Inactive state - pill-shaped with border
        background-color: #f5f5f5 !important;
        border: 1px solid #e0e0e0 !important;
        color: #666 !important;

        & i {
          color: #666 !important;
        }

        // Icon styling - CSS mask technique for dynamic coloring
        // The SVG acts as a mask shape, background-color fills it
        // Using <span> instead of <img> because span has no image content to render,
        // so only the background-color (through the mask) will be visible
        & .tab-icon {
          // Fixed dimensions (span has no intrinsic size)
          display: inline-block;
          width: 22px;
          height: 22px;

          // Mask properties
          -webkit-mask-size: contain;
          mask-size: contain;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-position: center;
          mask-position: center;

          // Icon takes on the text color (gray for inactive)
          background-color: #666;
          transition: background-color 0.2s ease;
        }

        // Hide default Vuetify overlay
        &:before {
          display: none !important;
        }

        // Hover state (inactive only)
        &:hover:not(.v-btn--active) {
          background-color: #eeeeee !important;
          border-color: #d0d0d0 !important;
        }

        // Disabled state
        &.v-btn--disabled {
          opacity: 0.5;
          color: rgba(158, 158, 158, 0.5) !important;

          & .tab-icon {
            background-color: rgba(158, 158, 158, 0.5);
          }
        }

        // Active state - outlined with primary border
        &.v-btn--active {
          background-color: transparent !important;
          border: 2px solid var(--v-primary-base) !important;
          color: var(--v-primary-base) !important;
          font-weight: 600;
          box-shadow: none !important;

          & i {
            color: var(--v-primary-base) !important;
          }

          // Icon matches primary text color exactly
          & .tab-icon {
            background-color: var(--v-primary-base);
          }
        }
      }
    }
  }

  // Dark theme support for dashboard tabs
  .theme--dark #dashboard-tabs {
    & .v-btn {
      background-color: rgba(255, 255, 255, 0.08) !important;
      border-color: rgba(255, 255, 255, 0.12) !important;
      color: rgba(255, 255, 255, 0.7) !important;

      & i {
        color: rgba(255, 255, 255, 0.7) !important;
      }

      // Icon matches light text for dark theme
      & .tab-icon {
        background-color: rgba(255, 255, 255, 0.7);
      }

      &:hover:not(.v-btn--active) {
        background-color: rgba(255, 255, 255, 0.12) !important;
        border-color: rgba(255, 255, 255, 0.2) !important;
      }

      &.v-btn--active {
        background-color: transparent !important;
        border-color: var(--v-primary-base) !important;
        color: var(--v-primary-base) !important;

        & i {
          color: var(--v-primary-base) !important;
        }

        // Icon matches primary text color exactly
        & .tab-icon {
          background-color: var(--v-primary-base);
        }
      }
    }
  }
</style>
